import * as React from 'react';
import TextButton, { TextButtonContentContainer, TextButtonProps } from './TextButton';
import { SystemIcons } from '../icons';
import { DropdownContainer } from '../Dropdown/CommonStyling';
import { nanoid } from 'nanoid';
import { Size } from '../types';
import DropdownContent, { DropdownCustomizationProps } from '../Dropdown/DropdownContent';
import styled from 'styled-components';
import { useClickOutsideRef, useFocusOutsideRef } from '../common';

const DualButtonsContainer = styled.div<{ $size?: Size }>`
  display: flex;
  align-items: center;

  button:first-child {
    margin-right: 2px;
  }

  ${TextButtonContentContainer} {
    min-width: unset;
    gap: 0px;
    padding: ${(props) => (props.$size == Size.Large ? '4px 18px' : props.$size == Size.Medium ? '4px 14px' : '4px 10px')};
  }

  .extraMargin {
    margin-bottom: 4px;
  }
`;

export interface ButtonOption {
  label: string;
}

export interface DualButtonProps extends TextButtonProps {
  /**
  * Required. An object containing customization properties for the dropdown.
  */
  dropdownCustomizationProps: DropdownCustomizationProps;
  /**
  * Optional. An array of strings representing the values in the dropdown menu.
  */
  dropdownMenuValues?: string[];
  /**
   * Optional. A function to be called when the values in the dropdown menu change.
   * It should return void, a boolean, or undefined.
   */
  setDropdownMenuValues?: (values: string[]) => void | boolean | undefined;
}

const DualFunctionButton: React.FunctionComponent<DualButtonProps> = ({
  children = 'Primary Button',
  variant = 'primary',
  type = 'button',
  size = Size.Medium,
  width: width = 'auto',
  testId,
  disabled,
  id,
  dropdownMenuValues,
  setDropdownMenuValues,
  dropdownCustomizationProps,
  ...props
}) => {
  // Let's filter out properties that we don't need to render.
  const { loading, icon, onClick, ...renderProps } = props;
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [activeDescendant, setActiveDescendant] = React.useState<string>();
  const [focused, setFocused] = React.useState<number | null>(null);
  const [keyboardNavigated, setKeyboardNavigated] = React.useState<boolean>(false);
  const dropdownContainerRef = React.useRef<HTMLDivElement>(null);
  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);

  const dropdownContentRef = React.useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useClickOutsideRef(
    () => setIsOpen(false),
    [dropdownContainerRef],
    useFocusOutsideRef(() => setIsOpen(false), [dropdownContentRef]),
  );
  
  const dropdownContentId = id ? id + '_dropdownContent' : nanoid();

  React.useEffect(() => {
    !isOpen && setKeyboardNavigated(false);
  }, [isOpen]);

  return (
    <DualButtonsContainer $size={size}>
      <DropdownContainer ref={dropdownContainerRef} $isButton={true} $readOnly={false} $disabled={false} margin="">
        <TextButton {...renderProps} testId={testId} onClick={onClick} disabled={disabled} type={type} loading={loading} icon={icon} size={size} width={width} variant={variant} flatEdge="right">
          {children}
        </TextButton>
        <DropdownContent
          containerRef={dropdownContainerRef}
          customizationProps={dropdownCustomizationProps}
          filter=""
          ref={dropdownContentRef}
          selectedValues={dropdownCustomizationProps.itemsType == 'normal' ? [] : dropdownMenuValues ?? selectedValues}
          setSelectedValues={setDropdownMenuValues ? setDropdownMenuValues : setSelectedValues}
          isButton={true}
          setIsOpen={setIsOpen}
          focused={focused}
          width={size == Size.Small ? '280px' : '360px'}
          onActiveDescendantChanged={(e) => setActiveDescendant(e)}
          setFocused={setFocused}
          size={size}
          outline={keyboardNavigated}
          isOpen={isOpen}
          id={dropdownContentId}
          messageOnNoResults=""
          alignLeft={true}
          ariaRolesType="menu"
        />
      </DropdownContainer>
      <TextButton
        variant={variant}
        {...renderProps}
        onKeyPress={(e: any) => {
          if (isOpen) return;

          if (e.key === 'Enter' || e.key === ' ') {
            setFocused(0);
            setKeyboardNavigated(true);
          }
        }}
        className="extraMargin"
        testId={'options_toggleBtn'}
        ref={dropdownButtonRef}
        aria-activedescendant={activeDescendant}
        role="button"
        aria-controls={dropdownContentId}
        disabled={disabled}
        type={type}
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        icon={ isOpen ? <SystemIcons.ArrowDropUp aria-hidden="true" size="16px" /> 
        : <SystemIcons.ArrowDropDown aria-hidden="true" size="16px" /> }
        flatEdge="left"
      />
    </DualButtonsContainer>
  );
};

export default DualFunctionButton;
